import { useState } from 'react';
import { Text, Modal, Button } from '@rsos/base-components';
import {
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@rsos/base-components/Modal/ModalComponents';
import AgencyListItem from './AgencyListItem';
import AgencySearchInput from './AgencySearchInput';
import TransferInfoBanner from './TransferInfoBanner';
import { AgencyList } from './TransferToAgencyModal.styles';
import type { Agency } from './types';

interface TransferToAgencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInitiateTransfer: (agency: Agency) => void;
  agencies: Agency[];
  isTransferLoading?: boolean;
  isLoadingAgencies?: boolean;
  isError?: boolean;
}

function TransferToAgencyModal({
  isOpen,
  onClose,
  onInitiateTransfer,
  agencies,
  isTransferLoading,
  isLoadingAgencies,
  isError,
}: TransferToAgencyModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgencyId, setSelectedAgencyId] = useState<string | null>(null);

  const filteredAgencies = agencies.filter(agency =>
    agency.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleClose = () => {
    setSearchQuery('');
    setSelectedAgencyId(null);
    onClose();
  };

  const handleInitiateTransfer = () => {
    if (selectedAgencyId) {
      const selectedAgency = agencies.find(
        agency => agency.identifier === selectedAgencyId,
      );
      if (!selectedAgency) {
        // should never ever happen since the selected id is from the list of agencies, but just in case:
        throw new Error('Selected agency not found');
      }
      onInitiateTransfer(selectedAgency);
    }
  };

  return (
    <Modal
      isOpened={isOpen}
      onClose={handleClose}
      width="400px"
      padding="0"
      fitContent
      id="modal-transfer-to-agency"
    >
      <ModalHeader
        onClose={handleClose}
        padding="24px 24px 16px"
        borderBottom="1px solid"
        borderColor="lightLineDivider"
      >
        <Text size="xlarge" color="primaryText" mb={1}>
          Transfer to Agency
        </Text>
        <Text size="small" color="secondaryText">
          Select destination ECC to transfer
        </Text>
      </ModalHeader>
      <ModalBody px="24px" mb={0}>
        <AgencySearchInput value={searchQuery} onChange={setSearchQuery} />
        <AgencyList>
          {isLoadingAgencies && (
            <Text
              size="small"
              color="secondaryText"
              p="16px"
              textAlign="center"
            >
              Loading agencies...
            </Text>
          )}
          {isError && (
            <Text size="small" color="error" p="16px" textAlign="center">
              Failed to load agencies. Please try again.
            </Text>
          )}
          {!isLoadingAgencies && !isError && filteredAgencies.length === 0 && (
            <Text
              size="small"
              color="secondaryText"
              p="16px"
              textAlign="center"
            >
              No agencies available
            </Text>
          )}
          {!isLoadingAgencies &&
            !isError &&
            filteredAgencies.map(agency => (
              <AgencyListItem
                key={agency.identifier}
                agency={agency}
                isSelected={selectedAgencyId === agency.identifier}
                onSelect={setSelectedAgencyId}
              />
            ))}
        </AgencyList>
      </ModalBody>
      <ModalFooter
        py="16px"
        px="24px"
        borderTop="1px solid"
        borderColor="lightLineDivider"
        stacked
        flow={12}
      >
        {selectedAgencyId && <TransferInfoBanner />}
        <div style={{ display: 'flex', gap: 12, width: '100%' }}>
          <Button btnType="tertiary" grow onClick={handleClose}>
            Cancel
          </Button>
          <Button
            btnType="primary"
            grow
            onClick={handleInitiateTransfer}
            disabled={!selectedAgencyId}
            isLoadingProp={isTransferLoading}
          >
            Initiate Transfer
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

export default TransferToAgencyModal;
